import React, { useEffect, useState } from 'react'
import { Check, ChevronLeft, ChevronRight, Loader2, Package, Truck, X } from 'lucide-react'
import { apiClient } from '../config/api'
import {
  ASSET_TYPE_OPTIONS, VEHICLE_ICON_OPTIONS, defaultIconForType, vehicleIconComponent
} from '../utils/vehiclePresentation'
import type { AssetType, VehicleIconKey } from '../utils/vehiclePresentation'
import TransportRoutePicker, {
  TransportRouteSelection, applyTransportSelection, emptyRouteSelection, routeSelectionComplete
} from './TransportRoutePicker'

type Source = 'ASSET' | 'CONTAINER'

interface Props {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
  showMessage?: (message: string, type?: 'success' | 'error' | 'warning' | 'info') => void
  initialCustomerId?: number | null
  initialSource?: Source
  lockCustomer?: boolean
}

interface Customer {
  id: number
  company_name: string
  customer_code: string
}

const VEHICLE_CATALOG: Record<AssetType, Record<string, string[]>> = {
  TRACTOR_HEAD: {
    'Mercedes-Benz': ['Actros', 'Arocs'],
    Volvo: ['FH', 'FM'],
    Scania: ['R-Series', 'S-Series'],
    MAN: ['TGX', 'TGS'],
    Iveco: ['S-Way'],
    'Renault Trucks': ['T', 'C']
  },
  TRUCK: {
    'Mercedes-Benz': ['Actros', 'Arocs', 'Atego'],
    Volvo: ['FH', 'FM', 'FMX'],
    Scania: ['R-Series', 'S-Series', 'P-Series'],
    MAN: ['TGX', 'TGS'],
    Iveco: ['S-Way', 'Eurocargo'],
    'Renault Trucks': ['T', 'C', 'D']
  },
  VAN: {
    'Mercedes-Benz': ['Sprinter', 'Vito'],
    Ford: ['Transit'],
    Renault: ['Master', 'Trafic'],
    Toyota: ['HiAce'],
    Volkswagen: ['Crafter']
  },
  PICKUP: {
    Toyota: ['Hilux'],
    Ford: ['Ranger'],
    Isuzu: ['D-Max'],
    Mitsubishi: ['L200'],
    Nissan: ['Navara']
  },
  TRAILER: {
    'Schmitz Cargobull': ['S.CS', 'S.KO', 'S.KI'],
    Krone: ['Profi Liner', 'Cool Liner'],
    Kögel: ['Cargo', 'Cool'],
    Fruehauf: ['Curtainsider', 'Tanker']
  },
  CAR: {
    Toyota: ['Corolla', 'Land Cruiser'],
    Renault: ['Clio', 'Duster'],
    Peugeot: ['208', '3008'],
    Volkswagen: ['Golf', 'Tiguan']
  },
  TANKER: {
    Feldbinder: ['Chemical Tanker', 'Food Tanker', 'Fuel Tanker'],
    Kässbohrer: ['K.STB', 'K.STC'],
    Magyar: ['Fuel Tanker', 'Chemical Tanker'],
    Fruehauf: ['Tanker']
  },
  CONTAINER_CHASSIS: {
    Krone: ['Box Liner'],
    'Schmitz Cargobull': ['S.CF'],
    Kässbohrer: ['K.SHG'],
    Fruehauf: ['Container Chassis']
  },
  HYDRAULIC_EQUIPMENT: {
    Goldhofer: ['THP', 'STZ'],
    Faymonville: ['ModulMAX', 'CombiMAX'],
    Scheuerle: ['SPMT', 'K25'],
    Nicolas: ['MDED', 'Tractomas']
  }
}

const buildInitialForm = (customerId?: number | null, source?: Source) => ({
  customer_id: customerId != null ? String(customerId) : '',
  vehicle_source: (source || 'ASSET') as Source,
  asset_type: 'TRACTOR_HEAD' as AssetType,
  icon_key: 'tractor-head' as VehicleIconKey,
  brand: '',
  custom_brand: '',
  model: '',
  custom_model: '',
  custom_name: '',
  license_plate: '',
  year_manufactured: new Date().getFullYear(),
  equipment_reference: '',
  container_type: '20GP',
  owner: '',
  origin: '',
  destination: '',
  booking_reference: '',
  length: '',
  width: '',
  height: '',
  max_weight: '',
  engine_type: 'DIESEL',
  fuel_capacity: '',
  consumption: '',
  max_speed: '',
  initial_odometer_km: ''
})

const VehicleCreationWizard: React.FC<Props> = ({
  isOpen,
  onClose,
  onSuccess,
  showMessage,
  initialCustomerId = null,
  initialSource,
  lockCustomer = false
}) => {
  const [step, setStep] = useState(1)
  const [customers, setCustomers] = useState<Customer[]>([])
  const [form, setForm] = useState(() => buildInitialForm(initialCustomerId, initialSource))
  const [route, setRoute] = useState<TransportRouteSelection>(emptyRouteSelection)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!isOpen) return
    const next = buildInitialForm(initialCustomerId, initialSource)
    setForm(next)
    setRoute(emptyRouteSelection())
    setStep(1)
    apiClient.get('/customers')
      .then(response => {
        const list = Array.isArray(response.data) ? response.data : response.data?.customers || []
        setCustomers(list)
      })
      .catch(() => setCustomers([]))
  }, [isOpen, initialCustomerId, initialSource, lockCustomer])

  useEffect(() => {
    const brands = Object.keys(VEHICLE_CATALOG[form.asset_type])
    setForm(current => ({
      ...current,
      brand: brands[0] || '',
      model: brands[0] ? VEHICLE_CATALOG[form.asset_type][brands[0]][0] : '',
      icon_key: defaultIconForType(form.asset_type)
    }))
  }, [form.asset_type])

  const selectedCustomer = customers.find(customer => String(customer.id) === form.customer_id)
  const brands = Object.keys(VEHICLE_CATALOG[form.asset_type])
  const models = form.brand === 'OTHER' ? [] : (VEHICLE_CATALOG[form.asset_type][form.brand] || [])
  const effectiveBrand = form.brand === 'OTHER' ? form.custom_brand.trim() : form.brand
  const effectiveModel = form.model === 'OTHER' ? form.custom_model.trim() : form.model
  const detailsLabel = form.vehicle_source === 'CONTAINER' ? 'Container details' : 'Asset details'
  const stepLabels = ['Customer', detailsLabel, 'Review']

  if (!isOpen) return null

  const setValue = (key: keyof typeof form, value: string | number) => {
    setForm(current => ({ ...current, [key]: value }))
  }

  const goNext = () => {
    if (step === 1 && !form.customer_id) {
      showMessage?.('Select the customer that will own this vehicle', 'warning')
      return
    }
    if (step === 2) {
      if (form.vehicle_source === 'ASSET' && (!effectiveBrand || !effectiveModel)) {
        showMessage?.('Select the asset brand and model', 'warning')
        return
      }
      if (form.vehicle_source === 'CONTAINER') {
        if (!form.equipment_reference.trim()) {
          showMessage?.('Enter the container equipment reference', 'warning')
          return
        }
        if (!form.owner.trim()) {
          showMessage?.('Enter the container owner / shipping line', 'warning')
          return
        }
        if (!form.destination.trim()) {
          showMessage?.('Select the container itinerary or destination', 'warning')
          return
        }
      }
    }
    setStep(current => Math.min(3, current + 1))
  }

  const submit = async () => {
    setSaving(true)
    try {
      const numeric = (value: string) => value === '' ? null : Number(value)
      const payload = {
        vehicle_source: form.vehicle_source,
        customer_id: Number(form.customer_id),
        asset_type: form.asset_type,
        icon_key: form.vehicle_source === 'CONTAINER' ? 'container' : form.icon_key,
        brand: effectiveBrand,
        model: effectiveModel,
        custom_name: form.custom_name.trim() || null,
        license_plate: form.license_plate.trim() || null,
        year_manufactured: form.year_manufactured,
        equipment_reference: form.equipment_reference.trim(),
        container_type: form.container_type,
        owner: form.owner.trim() || null,
        origin: form.origin.trim() || null,
        destination: form.destination.trim() || null,
        booking_reference: form.booking_reference.trim() || null,
        length: numeric(form.length),
        width: numeric(form.width),
        height: numeric(form.height),
        max_weight: numeric(form.max_weight),
        engine_type: form.engine_type,
        fuel_capacity: numeric(form.fuel_capacity),
        consumption: numeric(form.consumption),
        max_speed: numeric(form.max_speed),
        initial_odometer_km: numeric(form.initial_odometer_km)
      }
      const response = await apiClient.post('/vehicles', payload)
      // Also register the transport assignment so the route progress bar works
      if (form.vehicle_source === 'CONTAINER' && routeSelectionComplete(route) && response.data?.vehicle_id) {
        try {
          await applyTransportSelection('CONTAINER', Number(response.data.vehicle_id), route)
        } catch {
          showMessage?.('Container created, but the transport assignment failed — assign it from the vehicle card', 'warning')
        }
      }
      showMessage?.(`${response.data.vehicle_code} created for ${selectedCustomer?.company_name}`, 'success')
      setForm(buildInitialForm(initialCustomerId, initialSource))
      setRoute(emptyRouteSelection())
      setStep(1)
      onSuccess()
    } catch (error: any) {
      showMessage?.(error.response?.data?.error || 'Vehicle creation failed', 'error')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-[320] flex items-center justify-center p-4 bg-slate-950/45">
      <div className="w-full max-w-3xl max-h-[88vh] overflow-hidden rounded-2xl bg-white shadow-2xl flex flex-col">
        <header className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <div>
            <h2 className="text-lg font-bold text-slate-900">Create asset or container</h2>
            <p className="text-xs text-slate-500">Customer ownership first. The permanent asset code is generated automatically.</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg text-slate-500 hover:bg-slate-100"><X size={19} /></button>
        </header>

        <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2">
          {stepLabels.map((label, index) => {
            const number = index + 1
            return (
              <React.Fragment key={`${label}-${number}`}>
                {index > 0 && <span className="h-px flex-1 bg-slate-200" />}
                <span className={`inline-flex items-center gap-1.5 text-xs font-semibold ${step >= number ? 'text-blue-700' : 'text-slate-400'}`}>
                  <span className={`w-6 h-6 rounded-full flex items-center justify-center ${step >= number ? 'bg-blue-600 text-white' : 'bg-slate-100'}`}>
                    {step > number ? <Check size={13} /> : number}
                  </span>
                  <span className="hidden sm:inline">{label}</span>
                </span>
              </React.Fragment>
            )
          })}
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {step === 1 && (
            <div className="space-y-5">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Customer owner *</label>
                {lockCustomer && form.customer_id ? (
                  <div className="rounded-xl border border-blue-200 bg-blue-50 px-3 py-3">
                    <div className="text-sm font-semibold text-blue-900">{selectedCustomer?.company_name || 'Selected customer'}</div>
                    <div className="text-xs text-blue-700">{selectedCustomer?.customer_code || `ID ${form.customer_id}`}</div>
                  </div>
                ) : (
                  <select
                    value={form.customer_id}
                    onChange={event => setValue('customer_id', event.target.value)}
                    className="w-full px-3 py-3 rounded-xl border border-slate-300 bg-white text-sm outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select a customer by company or code</option>
                    {customers.map(customer => (
                      <option key={customer.id} value={customer.id}>
                        {customer.company_name} — {customer.customer_code}
                      </option>
                    ))}
                  </select>
                )}
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Create</label>
                <div className="grid grid-cols-2 gap-3">
                  <button type="button" onClick={() => setValue('vehicle_source', 'ASSET')}
                    className={`p-4 rounded-xl border text-left ${form.vehicle_source === 'ASSET' ? 'border-blue-400 bg-blue-50' : 'border-slate-200'}`}>
                    <Truck size={22} className="mb-2 text-blue-600" />
                    <span className="block text-sm font-bold">Asset</span>
                    <span className="block text-xs text-slate-500">Vehicle, fuel cistern trailer or container chassis</span>
                  </button>
                  <button type="button" onClick={() => setValue('vehicle_source', 'CONTAINER')}
                    className={`p-4 rounded-xl border text-left ${form.vehicle_source === 'CONTAINER' ? 'border-blue-400 bg-blue-50' : 'border-slate-200'}`}>
                    <Package size={22} className="mb-2 text-blue-600" />
                    <span className="block text-sm font-bold">Container</span>
                    <span className="block text-xs text-slate-500">Equipment tracked as part of the fleet</span>
                  </button>
                </div>
              </div>
            </div>
          )}

          {step === 2 && form.vehicle_source === 'ASSET' && (
            <div className="space-y-5">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Asset type</label>
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                  {ASSET_TYPE_OPTIONS.map(item => {
                    const Icon = vehicleIconComponent(item.iconKey, item.value)
                    return <button key={item.value} type="button" onClick={() => setValue('asset_type', item.value)}
                      className={`p-3 rounded-xl border flex flex-col items-center gap-1 text-xs font-semibold ${
                        form.asset_type === item.value ? 'border-blue-400 bg-blue-50 text-blue-700' : 'border-slate-200 text-slate-600'
                      }`}>
                      <Icon size={24} aria-hidden="true" />{item.label}
                    </button>
                  })}
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Map and fleet icon</label>
                <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                  {VEHICLE_ICON_OPTIONS.filter(option => option.key !== 'container').map(option => (
                    <button key={option.key} type="button" onClick={() => setValue('icon_key', option.key)}
                      className={`p-2.5 rounded-xl border flex flex-col items-center gap-1 text-[11px] font-semibold ${
                        form.icon_key === option.key ? 'border-blue-400 bg-blue-50 text-blue-700' : 'border-slate-200 text-slate-600'
                      }`}>
                      <option.Icon size={22} aria-hidden="true" />{option.label}
                    </button>
                  ))}
                </div>
                <p className="mt-1.5 text-xs text-slate-500">The icon shape is selectable; its color is controlled by live tracking status.</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <label className="text-sm font-medium text-slate-700">Brand *
                  <select value={form.brand} onChange={event => {
                    const brand = event.target.value
                    setForm(current => ({ ...current, brand, model: brand === 'OTHER' ? 'OTHER' : (VEHICLE_CATALOG[current.asset_type][brand]?.[0] || '') }))
                  }} className="mt-1.5 w-full px-3 py-2.5 rounded-xl border border-slate-300 bg-white">
                    {brands.map(brand => <option key={brand}>{brand}</option>)}
                    <option value="OTHER">Other</option>
                  </select>
                  {form.brand === 'OTHER' && <input value={form.custom_brand} onChange={event => setValue('custom_brand', event.target.value)}
                    placeholder="Brand name" className="mt-2 w-full px-3 py-2.5 rounded-xl border border-slate-300" />}
                </label>
                <label className="text-sm font-medium text-slate-700">Model *
                  <select value={form.model} onChange={event => setValue('model', event.target.value)}
                    className="mt-1.5 w-full px-3 py-2.5 rounded-xl border border-slate-300 bg-white">
                    {models.map(model => <option key={model}>{model}</option>)}
                    <option value="OTHER">Other</option>
                  </select>
                  {form.model === 'OTHER' && <input value={form.custom_model} onChange={event => setValue('custom_model', event.target.value)}
                    placeholder="Model name" className="mt-2 w-full px-3 py-2.5 rounded-xl border border-slate-300" />}
                </label>
                <label className="text-sm font-medium text-slate-700">License plate
                  <input value={form.license_plate} onChange={event => setValue('license_plate', event.target.value.toUpperCase())}
                    className="mt-1.5 w-full px-3 py-2.5 rounded-xl border border-slate-300" />
                </label>
                <label className="text-sm font-medium text-slate-700">Display name
                  <input value={form.custom_name} onChange={event => setValue('custom_name', event.target.value)}
                    placeholder="Optional operational name" className="mt-1.5 w-full px-3 py-2.5 rounded-xl border border-slate-300" />
                </label>
                <label className="text-sm font-medium text-slate-700">Year
                  <input type="number" min={1980} max={new Date().getFullYear() + 1} value={form.year_manufactured}
                    onChange={event => setValue('year_manufactured', Number(event.target.value))}
                    className="mt-1.5 w-full px-3 py-2.5 rounded-xl border border-slate-300" />
                </label>
                <label className="text-sm font-medium text-slate-700">Engine
                  <select value={form.engine_type} onChange={event => setValue('engine_type', event.target.value)}
                    className="mt-1.5 w-full px-3 py-2.5 rounded-xl border border-slate-300 bg-white">
                    <option>DIESEL</option><option>GASOLINE</option><option>ELECTRIC</option><option>HYBRID</option>
                  </select>
                </label>
                <label className="text-sm font-medium text-slate-700">Average consumption
                  <input type="number" min={0} step="0.1" value={form.consumption}
                    onChange={event => setValue('consumption', event.target.value)}
                    placeholder="L/100km"
                    className="mt-1.5 w-full px-3 py-2.5 rounded-xl border border-slate-300" />
                  <span className="mt-1 block text-xs font-normal text-slate-500">
                    Needed for the fuel and CO2 report; left empty, that report reports it as unavailable.
                  </span>
                </label>
                <label className="text-sm font-medium text-slate-700">Odometer today
                  <input type="number" min={0} step="1" value={form.initial_odometer_km}
                    onChange={event => setValue('initial_odometer_km', event.target.value)}
                    placeholder="km"
                    className="mt-1.5 w-full px-3 py-2.5 rounded-xl border border-slate-300" />
                  <span className="mt-1 block text-xs font-normal text-slate-500">
                    The tracker only counts distance from installation, so mileage starts from this reading.
                  </span>
                </label>
              </div>
            </div>
          )}

          {step === 2 && form.vehicle_source === 'CONTAINER' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2 rounded-xl border border-blue-200 bg-blue-50 px-3 py-2.5 text-xs text-blue-800">
                ISO shipping containers only. A road fuel cistern/tanker trailer must be created as an Asset.
              </div>
              <label className="text-sm font-medium text-slate-700">Equipment reference *
                <input value={form.equipment_reference} onChange={event => setValue('equipment_reference', event.target.value.toUpperCase())}
                  placeholder="e.g. MSCU1234567" className="mt-1.5 w-full px-3 py-2.5 rounded-xl border border-slate-300" />
              </label>
              <label className="text-sm font-medium text-slate-700">Container type *
                <select value={form.container_type} onChange={event => setValue('container_type', event.target.value)}
                  className="mt-1.5 w-full px-3 py-2.5 rounded-xl border border-slate-300 bg-white">
                  <option value="20GP">20GP — 20ft general purpose</option>
                  <option value="40GP">40GP — 40ft general purpose</option>
                  <option value="40HC">40HC — 40ft high cube</option>
                  <option value="20RF">20RF — 20ft reefer</option>
                  <option value="40RF">40RF — 40ft reefer</option>
                  <option value="45HC">45HC — 45ft high cube</option>
                  <option value="FLAT">FLAT — flat rack</option>
                  <option value="TANK">TANK — tank container</option>
                </select>
              </label>
              <label className="text-sm font-medium text-slate-700 sm:col-span-2">Owner / shipping line *
                <input value={form.owner} onChange={event => setValue('owner', event.target.value)}
                  placeholder="Owner company" className="mt-1.5 w-full px-3 py-2.5 rounded-xl border border-slate-300" />
              </label>
              <div className="sm:col-span-2 rounded-xl border border-slate-200 bg-slate-50 p-3">
                <div className="text-sm font-semibold text-slate-700 mb-2">Transport route *</div>
                <TransportRoutePicker
                  value={route}
                  requireDestination
                  onChange={next => {
                    setRoute(next)
                    setForm(current => ({
                      ...current,
                      origin: next.departure,
                      destination: next.destination
                    }))
                  }}
                />
              </div>
              <label className="text-sm font-medium text-slate-700">Booking reference
                <input value={form.booking_reference} onChange={event => setValue('booking_reference', event.target.value)}
                  className="mt-1.5 w-full px-3 py-2.5 rounded-xl border border-slate-300" />
              </label>
              <label className="text-sm font-medium text-slate-700">Display name
                <input value={form.custom_name} onChange={event => setValue('custom_name', event.target.value)}
                  className="mt-1.5 w-full px-3 py-2.5 rounded-xl border border-slate-300" />
              </label>
            </div>
          )}

          {step === 3 && (
            <div className="max-w-xl mx-auto space-y-4">
              <div className="rounded-xl border border-blue-200 bg-blue-50 p-4">
                <div className="text-xs font-semibold text-blue-600 uppercase tracking-wide">Owner</div>
                <div className="mt-1 text-lg font-bold text-blue-950">{selectedCustomer?.company_name}</div>
                <div className="text-sm text-blue-700">{selectedCustomer?.customer_code}</div>
              </div>
              <div className="rounded-xl border border-slate-200 divide-y divide-slate-100">
                <div className="p-3 flex justify-between gap-4"><span className="text-sm text-slate-500">Object</span><span className="text-sm font-bold">{form.vehicle_source === 'ASSET' ? form.asset_type : 'CONTAINER'}</span></div>
                {form.vehicle_source === 'ASSET' && <div className="p-3 flex justify-between gap-4"><span className="text-sm text-slate-500">Icon</span><span className="text-sm font-bold">{VEHICLE_ICON_OPTIONS.find(option => option.key === form.icon_key)?.label}</span></div>}
                <div className="p-3 flex justify-between gap-4"><span className="text-sm text-slate-500">Identity</span><span className="text-sm font-bold text-right">{form.vehicle_source === 'ASSET' ? `${effectiveBrand} ${effectiveModel}` : form.equipment_reference}</span></div>
                {form.vehicle_source === 'CONTAINER' && (
                  <>
                    <div className="p-3 flex justify-between gap-4"><span className="text-sm text-slate-500">Type</span><span className="text-sm font-bold">{form.container_type}</span></div>
                    <div className="p-3 flex justify-between gap-4"><span className="text-sm text-slate-500">Shipping owner</span><span className="text-sm font-bold text-right">{form.owner}</span></div>
                    {route.corridorId && <div className="p-3 flex justify-between gap-4"><span className="text-sm text-slate-500">Itinerary</span><span className="text-sm font-bold text-right">{route.corridorName}</span></div>}
                    {form.origin.trim() && <div className="p-3 flex justify-between gap-4"><span className="text-sm text-slate-500">Departure</span><span className="text-sm font-bold text-right">{form.origin}</span></div>}
                    <div className="p-3 flex justify-between gap-4"><span className="text-sm text-slate-500">Destination</span><span className="text-sm font-bold text-right">{form.destination}</span></div>
                    {form.booking_reference.trim() && <div className="p-3 flex justify-between gap-4"><span className="text-sm text-slate-500">Booking</span><span className="text-sm font-bold text-right">{form.booking_reference}</span></div>}
                  </>
                )}
                <div className="p-3 flex justify-between gap-4"><span className="text-sm text-slate-500">Permanent code</span><span className="text-sm font-semibold text-blue-700">{form.vehicle_source === 'ASSET' ? 'Generated automatically after save' : form.equipment_reference}</span></div>
              </div>
              <p className="text-xs text-slate-500">Drivers are managed in Staff and missions. Creating this record does not assign a driver.</p>
            </div>
          )}
        </div>

        <footer className="px-5 py-4 border-t border-slate-200 flex items-center justify-between">
          <button type="button" onClick={step === 1 ? onClose : () => setStep(current => current - 1)}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl border border-slate-300 text-sm font-semibold text-slate-700">
            {step > 1 && <ChevronLeft size={16} />}{step === 1 ? 'Cancel' : 'Back'}
          </button>
          {step < 3 ? (
            <button type="button" onClick={goNext} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-blue-600 text-white text-sm font-semibold">
              Continue <ChevronRight size={16} />
            </button>
          ) : (
            <button type="button" onClick={submit} disabled={saving}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-blue-600 text-white text-sm font-semibold disabled:opacity-50">
              {saving ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
              Create {form.vehicle_source === 'ASSET' ? 'asset' : 'container'}
            </button>
          )}
        </footer>
      </div>
    </div>
  )
}

export default VehicleCreationWizard
